// web/src/pages/admin/lookups/TransactionTypesPage.tsx
//
// I-14 — Admin CRUD for transaction-type codes (`/api/admin/transaction-types`).
//
// Foundation rows (codes the backend's TransactionTypeCodes.PlanIWriteable
// hashset locks against deletion) are displayed with a lock indicator and
// have their delete button disabled. NB the SPA mirrors the backend constant
// here — no API surface exposes "is foundation". If you add or remove members
// from `TransactionTypeCodes.PlanIWriteable` in the .NET source, mirror the
// edit in `FOUNDATION_TRANSACTION_TYPES` below.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { z } from "zod";
import {
  listTransactionTypes,
  createTransactionType,
  updateTransactionType,
  deleteTransactionType,
  type TransactionTypeDto,
} from "@/lib/invoices-payments";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

// Mirror of Allmark.Domain.InvoicesPayments.TransactionTypeCodes.PlanIWriteable.
// Keep in sync with src/Allmark.Domain/InvoicesPayments/TransactionTypeCodes.cs.
export const FOUNDATION_TRANSACTION_TYPES: ReadonlySet<string> = new Set([
  "Invoice",
  "InvoiceCancellation",
]);

const schema = z.object({
  tranTypeCode: z.string().min(1).max(40),
  description: z.string().min(1).max(200),
  isCredit: z.boolean(),
  sortOrder: z.number().int().nullable(),
});
type Form = z.infer<typeof schema>;

export function TransactionTypesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<TransactionTypeDto | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ["transaction-types"],
    queryFn: () => listTransactionTypes(),
  });

  const upsert = useMutation({
    mutationFn: (form: Form) => editing
      ? updateTransactionType(editing.tranTypeCode, {
          description: form.description,
          isCredit: form.isCredit,
          sortOrder: form.sortOrder,
        })
      : createTransactionType({
          tranTypeCode: form.tranTypeCode,
          description: form.description,
          isCredit: form.isCredit,
          sortOrder: form.sortOrder,
        }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["transaction-types"] });
      setEditing(null);
      setCreating(false);
    },
  });

  const del = useMutation({
    mutationFn: (code: string) => deleteTransactionType(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["transaction-types"] }),
  });

  const cols: DataGridColumn<TransactionTypeDto>[] = [
    {
      id: "code",
      header: "Code",
      accessor: (r) => (
        <span className="inline-flex items-center gap-1">
          {FOUNDATION_TRANSACTION_TYPES.has(r.tranTypeCode) && (
            <Lock aria-label="Foundation row (delete locked)" className="size-3 text-muted-foreground" />
          )}
          {r.tranTypeCode}
        </span>
      ),
    },
    { id: "desc", header: "Description", accessor: (r) => r.description },
    { id: "credit", header: "Credit", accessor: (r) => (r.isCredit ? "Yes" : "—") },
    { id: "sort", header: "Sort", accessor: (r) => r.sortOrder ?? "—", align: "right" },
    {
      id: "actions",
      header: "",
      accessor: (r) => {
        const isFoundation = FOUNDATION_TRANSACTION_TYPES.has(r.tranTypeCode);
        return (
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Edit ${r.tranTypeCode}`}
              onClick={() => setEditing(r)}
            >
              Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Delete ${r.tranTypeCode}`}
              disabled={isFoundation || del.isPending}
              onClick={() => {
                if (!isFoundation) del.mutate(r.tranTypeCode);
              }}
            >
              Delete
            </Button>
          </div>
        );
      },
      align: "right",
    },
  ];

  const drawerOpen = creating || editing !== null;
  const defaultValues: Form = editing
    ? {
        tranTypeCode: editing.tranTypeCode,
        description: editing.description,
        isCredit: editing.isCredit,
        sortOrder: editing.sortOrder,
      }
    : { tranTypeCode: "", description: "", isCredit: false, sortOrder: null };

  return (
    <ListPage
      title="Transaction types"
      primaryAction={
        <IntentButton onClick={() => setCreating(true)}>+ New</IntentButton>
      }
    >
      <DataGrid
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => r.tranTypeCode}
        totalRows={list.data?.length ?? 0}
        pageIndex={0}
        pageSize={Math.max(50, list.data?.length ?? 0)}
        loading={list.isLoading}
        onPageChange={() => undefined}
      />
      <DetailsDrawer
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={editing ? "Edit transaction type" : "New transaction type"}
      >
        <EntityForm<Form>
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              <div>
                <Label htmlFor="tt-code">Code</Label>
                <Input id="tt-code" disabled={editing !== null} {...form.register("tranTypeCode")} />
              </div>
              <div>
                <Label htmlFor="tt-desc">Description</Label>
                <Input id="tt-desc" {...form.register("description")} />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...form.register("isCredit")} /> Is credit
              </label>
              <div>
                <Label htmlFor="tt-sort">Sort order</Label>
                <Input
                  id="tt-sort"
                  type="number"
                  {...form.register("sortOrder", {
                    setValueAs: (v: string) => (v === "" ? null : Number(v)),
                  })}
                />
              </div>
              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailsDrawer>
    </ListPage>
  );
}
